import type { ComboboxData } from "@mantine/core";
import { IconArrowUpRight } from "@tabler/icons-react";
import { type MRT_ColumnDef } from "mantine-react-table-open";
import { generatePath } from "react-router";
import { useProjectBySiteId } from "../../../../../hooks/useProjectBySiteId";
import { MantineActionLinkCell } from "../../../../../mantine/mrt/components/cell/MantineActionLinkCell/MantineActionLinkCell";
import { ControlledMantineEditSelect } from "../../../../../mantine/mrt/components/edit/ControlledMantineEditSelect";
import { isMrtCreating } from "../../../../../mantine/mrt/utils/isMrtCreating";
import { ROUTES } from "../../../../../routes";
import type { ValidationErrors } from "../../../../../types/inlineEditTable.types";
import { COMPONENTS_PANEL_ID } from "../../../../StructureView/ComponentsPanel/ComponentsPanel";
import {
  GENERATOR_TAB_SEARCH_PARAM,
  GENERATOR_TABLE_TAB_OPTIONS
} from "../../../../StructureView/ComponentTabs/NonWizardTabs/GeneratorTab";
import type {
  DirektvermarktungGeneratorShareResponse,
  DirektvermarktungUnassignedGeneratorsResponse
} from "../../../Direktvermarktung.types";

type UnassignedGeneratorColumnProps = {
  validationErrors: ValidationErrors;
  unassignedGenerators?: Array<DirektvermarktungUnassignedGeneratorsResponse>;
  onChange: (generator: DirektvermarktungUnassignedGeneratorsResponse) => void;
  enableEditing?: boolean;
};

export function getGeneratorColumn({
  validationErrors,
  unassignedGenerators,
  onChange
}: UnassignedGeneratorColumnProps): MRT_ColumnDef<DirektvermarktungGeneratorShareResponse> {
  const unassignedGeneratorsChoices: ComboboxData =
    unassignedGenerators
      ?.map((generator) => ({
        value: generator.generator_display_name,
        label: generator.generator_display_name
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  return {
    accessorKey: "generator_display_name",
    header: "Erzeuger",
    enableSorting: true,
    Edit: ({ cell, column, row, table }) => (
      <ControlledMantineEditSelect
        cell={cell}
        columnId={column.id}
        data={unassignedGeneratorsChoices}
        errorText={validationErrors.generatorId}
        label="Erzeuger"
        overrideValue={
          isMrtCreating(table) ? row.original.generator_display_name : undefined
        }
        required
        row={row}
        onChange={(generatorName) => {
          const selectedGenerator = unassignedGenerators?.find(
            (generator) => generator.generator_display_name === generatorName
          );

          if (selectedGenerator) {
            onChange(selectedGenerator);
          }
        }}
      />
    ),
    Cell: ({ renderedCellValue, row }) => {
      const { data } = useProjectBySiteId({
        siteId: row.original.site_id
      });
      const projectId = data?.id;
      if (!row.original.site_id || !projectId) {
        return <>{renderedCellValue}</>;
      }

      const to =
        generatePath(ROUTES.site.generators, {
          projectId: projectId,
          siteId: String(row.original.site_id)
        }) +
        `?${GENERATOR_TAB_SEARCH_PARAM}=${GENERATOR_TABLE_TAB_OPTIONS.find((option) => option.value === "eeg-anlage")?.value}#${COMPONENTS_PANEL_ID}`;

      return (
        <MantineActionLinkCell
          IconComponent={IconArrowUpRight}
          renderedCellValue={renderedCellValue}
          to={to}
          tooltip="Zur Liegenschaft mit diesem Erzeuger"
        />
      );
    }
  };
}
